import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, X, Loader2, Check } from 'lucide-react';

// Add CSS animation for spinner
const spinStyle = document.createElement('style');
spinStyle.innerHTML = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
if (!document.head.querySelector('#enhanced-field-spin-style')) {
  spinStyle.id = 'enhanced-field-spin-style';
  document.head.appendChild(spinStyle);
}

const EnhancedFieldWithSuggestions = ({
  label,
  value,
  placeholder,
  onChange,
  isTextarea = false,
  fieldKey,
  collegeId,
  collegeName,
  aiSuggestions = null,
  onGenerateSuggestions
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState(aiSuggestions);
  const [isSaving, setIsSaving] = useState(false);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  const [isInteractingWithSuggestions, setIsInteractingWithSuggestions] = useState(false);
  const saveTimeoutRef = useRef(null);
  const savedIndicatorTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-resize textarea to fit content
  useEffect(() => {
    if (inputRef.current && isTextarea && inputRef.current.tagName === 'TEXTAREA') {
      const adjustHeight = () => {
        const textarea = inputRef.current;
        // Reset height to recalculate
        textarea.style.height = '120px'; // Set to min height first
        // Set height to scrollHeight to fit all content including wrapped lines
        const scrollHeight = textarea.scrollHeight;
        const newHeight = Math.min(500, Math.max(120, scrollHeight + 2)); // Respect max height of 500px
        textarea.style.height = `${newHeight}px`;
      };

      // Adjust on mount and whenever value changes
      adjustHeight();
      
      // Also adjust with a small delay to ensure DOM is ready
      setTimeout(adjustHeight, 10);
      
      // Trigger another adjustment after a longer delay for complex layouts
      setTimeout(adjustHeight, 100);
      
      // Also adjust on window resize to handle responsive changes
      window.addEventListener('resize', adjustHeight);
      return () => window.removeEventListener('resize', adjustHeight);
    }
  }, [tempValue, isTextarea, isEditing, showSuggestions]); // Added showSuggestions to dependencies

  // Sync suggestions when aiSuggestions prop changes
  useEffect(() => {
    console.log('🔍 EnhancedFieldWithSuggestions AI Suggestions Update:', {
      fieldKey,
      hasAiSuggestions: !!aiSuggestions,
      aiSuggestionsType: typeof aiSuggestions,
      aiSuggestionsContent: aiSuggestions,
      suggestionsCount: aiSuggestions?.suggestions?.length || 0
    });
    
    // Defensive check: if aiSuggestions is an object with legacy format, reject it
    if (aiSuggestions && typeof aiSuggestions === 'object' && 
        (aiSuggestions.success !== undefined || aiSuggestions.fieldKey !== undefined)) {
      console.error('🔍 ❌ LEGACY FORMAT DETECTED in aiSuggestions prop - ignoring to prevent React Error #31');
      console.error('🔍 ❌ Legacy object:', aiSuggestions);
      setSuggestions(null);
      return;
    }
    
    setSuggestions(aiSuggestions);
  }, [aiSuggestions, fieldKey]);
  
  // Sync tempValue with value prop when it changes from outside
  useEffect(() => {
    if (!isEditing) {
      setTempValue(value || '');
    }
  }, [value, isEditing]);

  // Show suggestions automatically when entering edit mode if they exist
  const handleStartEdit = () => {
    setIsEditing(true);
    // If we have existing suggestions (from either prop or local state), show them when entering edit mode
    const hasSuggestions = (suggestions?.suggestions && suggestions.suggestions.length > 0) ||
                          (aiSuggestions?.suggestions && aiSuggestions.suggestions.length > 0);
    if (hasSuggestions) {
      setShowSuggestions(true);
      console.log('🔍 Showing existing suggestions for field:', fieldKey);
    }
  };

  // Debounced autosave functionality
  const handleValueChange = (newValue) => {
    setTempValue(newValue);
    
    // Clear existing save timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Clear saved indicator timeout
    if (savedIndicatorTimeoutRef.current) {
      clearTimeout(savedIndicatorTimeoutRef.current);
      setShowSavedIndicator(false);
    }
    
    // Show saving indicator after a brief delay
    setIsSaving(true);
    
    // Set new save timeout (1 second debounce)
    saveTimeoutRef.current = setTimeout(() => {
      onChange(newValue);
      setIsSaving(false);
      setShowSavedIndicator(true);
      
      // Hide saved indicator after 2 seconds
      savedIndicatorTimeoutRef.current = setTimeout(() => {
        setShowSavedIndicator(false);
      }, 2000);
    }, 1000);
  };

  // Handle blur (when user clicks outside)
  const handleBlur = () => {
    // Don't blur if we're interacting with suggestions
    if (isInteractingWithSuggestions) {
      console.log('🛑 Preventing blur - interacting with suggestions');
      return;
    }
    
    // Clear any pending save timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Save immediately if value has changed
    if (tempValue !== value) {
      onChange(tempValue);
      setIsSaving(false);
      setShowSavedIndicator(true);
      
      // Hide saved indicator after 2 seconds
      savedIndicatorTimeoutRef.current = setTimeout(() => {
        setShowSavedIndicator(false);
      }, 2000);
    }
    
    setIsEditing(false);
    setShowSuggestions(false);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (savedIndicatorTimeoutRef.current) {
        clearTimeout(savedIndicatorTimeoutRef.current);
      }
    };
  }, []);

  const handleGenerateSuggestions = async () => {
    console.log('🔧 handleGenerateSuggestions called for:', fieldKey, label);
    setIsGenerating(true);
    setShowSuggestions(true);
    
    try {
      const context = {
        collegeName,
        collegeId,
        currentValue: tempValue || value,
        label
      };
      console.log('📤 Calling onGenerateSuggestions with context:', context);
      
      const newSuggestions = await onGenerateSuggestions(fieldKey, context);
      console.log('📥 Received suggestions:', newSuggestions);
      
      setSuggestions(newSuggestions);
    } catch (error) {
      console.error('❌ Error generating suggestions:', error);
      setSuggestions({
        error: 'Unable to generate suggestions at this time. Please try again.'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const applySuggestion = (suggestion) => {
    console.log('🎯 applySuggestion called:', { suggestion, currentValue: tempValue });
    const newValue = tempValue ? `${tempValue}\n\n${suggestion}` : suggestion;
    console.log('🎯 Setting new value:', newValue);
    setTempValue(newValue);
    setShowSuggestions(false);
    
    // Reset the interaction flag after a brief delay to allow the field to refocus
    setIsInteractingWithSuggestions(false);
    
    // Trigger the debounced save immediately for better UX
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    setIsSaving(true);
    onChange(newValue);
    
    // Ensure we stay in edit mode
    setIsEditing(true);
    
    // Refocus the input/textarea
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        // Move cursor to end of text
        const length = inputRef.current.value.length;
        inputRef.current.setSelectionRange(length, length);
      }
      
      setIsSaving(false);
      setShowSavedIndicator(true);
      
      // Hide saved indicator after 2 seconds
      savedIndicatorTimeoutRef.current = setTimeout(() => {
        setShowSavedIndicator(false);
      }, 2000);
    }, 100);
  };

  const SuggestionCallout = () => {
    if (!showSuggestions) return null;

    return (
      <div 
        style={{
          background: '#fffef7',
          border: '2px solid #fbbf24',
          borderRadius: '8px',
          padding: '12px',
          marginTop: '8px',
          position: 'relative'
        }}
        onMouseEnter={() => {
          console.log('🔵 Entering suggestions area');
          setIsInteractingWithSuggestions(true);
        }}
        onMouseLeave={() => {
          console.log('🔴 Leaving suggestions area');
          setIsInteractingWithSuggestions(false);
        }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '8px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: '#92400e',
            fontSize: '13px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.025em'
          }}>
            <Sparkles size={14} />
            Smart Suggestion
          </div>
          <button
            onClick={() => setShowSuggestions(false)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={14} color="#92400e" />
          </button>
        </div>

        {isGenerating ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#92400e',
            fontSize: '14px'
          }}>
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            Generating personalized suggestions...
          </div>
        ) : suggestions?.error ? (
          <div style={{ color: '#dc2626', fontSize: '14px' }}>
            {suggestions.error}
          </div>
        ) : suggestions?.suggestions && Array.isArray(suggestions.suggestions) ? (
          <div>
            {console.log('🎯 Rendering suggestions:', { 
              totalSuggestions: suggestions.suggestions.length,
              stringSuggestions: suggestions.suggestions.filter(s => typeof s === 'string').length,
              allSuggestions: suggestions.suggestions 
            })}
            {suggestions.suggestions.filter(s => typeof s === 'string').map((suggestion, index) => (
              <div
                key={index}
                style={{
                  fontSize: '14px',
                  color: '#451a03',
                  marginBottom: index < suggestions.suggestions.filter(s => typeof s === 'string').length - 1 ? '8px' : 0,
                  padding: '8px',
                  background: 'rgba(251, 191, 36, 0.1)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: '1px solid transparent',
                  userSelect: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(251, 191, 36, 0.2)';
                  e.currentTarget.style.borderColor = '#fbbf24';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(251, 191, 36, 0.1)';
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent blur from firing
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.background = 'rgba(251, 191, 36, 0.3)';
                  console.log('🖱️ Mouse down on suggestion');
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.background = 'rgba(251, 191, 36, 0.2)';
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🎯 Suggestion clicked:', suggestion);
                  applySuggestion(suggestion);
                }}
              >
                {suggestion}
                <div style={{
                  fontSize: '12px',
                  color: '#78716c',
                  marginTop: '4px',
                  fontStyle: 'italic',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>👆</span> Click to use this idea
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ 
            padding: '8px', 
            background: '#f3f4f6', 
            borderRadius: '4px', 
            fontSize: '12px', 
            color: '#6b7280' 
          }}>
            {console.log('🎯 No valid suggestions to render:', {
              suggestions,
              hasError: !!suggestions?.error,
              hasSuggestionsArray: !!(suggestions?.suggestions && Array.isArray(suggestions.suggestions)),
              suggestionsType: typeof suggestions?.suggestions
            })}
            No suggestions available in the expected format.
            {suggestions && (
              <details style={{ marginTop: '4px' }}>
                <summary style={{ cursor: 'pointer' }}>Debug Info</summary>
                <pre style={{ fontSize: '10px', marginTop: '4px' }}>
                  {JSON.stringify(suggestions, null, 2)}
                </pre>
              </details>
            )}
          </div>
        )}
      </div>
    );
  };

  if (isEditing) {
    return (
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '11px',
          fontWeight: 700,
          color: '#78716c',
          marginBottom: '8px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          {label}
          {isSaving && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '10px',
              color: '#a8a29e',
              fontWeight: 500
            }}>
              <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }} />
              Saving...
            </span>
          )}
          {showSavedIndicator && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '10px',
              color: '#10b981',
              fontWeight: 500
            }}>
              <Check size={10} />
              Saved
            </span>
          )}
        </label>
        <div style={{ position: 'relative' }}>
          {isTextarea ? (
            <textarea
              ref={inputRef}
              value={tempValue}
              onChange={(e) => handleValueChange(e.target.value)}
              onBlur={handleBlur}
              placeholder={placeholder}
              style={{
                width: '100%',
                minHeight: '120px',
                height: '120px', // Start at min height, will be adjusted by useEffect
                maxHeight: '500px', // Set a max height for very long content
                padding: '12px',
                paddingRight: '48px',
                border: '2px solid #e7e5e4',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'none',
                fontFamily: 'inherit',
                lineHeight: '1.5',
                overflowY: 'auto', // Show scrollbar if content exceeds maxHeight
                overflowX: 'hidden',
                wordWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
                boxSizing: 'border-box'
              }}
              autoFocus
            />
          ) : (
            <input
              ref={inputRef}
              type="text"
              value={tempValue}
              onChange={(e) => handleValueChange(e.target.value)}
              onBlur={handleBlur}
              placeholder={placeholder}
              style={{
                width: '100%',
                padding: '12px',
                paddingRight: '48px',
                border: '2px solid #e7e5e4',
                borderRadius: '8px',
                fontSize: '14px'
              }}
              autoFocus
            />
          )}
          
          {/* AI Suggestions Button */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault(); // This prevents the blur event from firing
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('🔘 Button clicked!');
              handleGenerateSuggestions();
            }}
            disabled={isGenerating}
            style={{
              position: 'absolute',
              right: '8px',
              top: '8px',
              background: '#fffef7',
              border: '1px solid #fbbf24',
              borderRadius: '6px',
              padding: '6px 10px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#92400e',
              cursor: isGenerating ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => !isGenerating && (e.target.style.background = '#fef3c7')}
            onMouseLeave={(e) => e.target.style.background = '#fffef7'}
          >
            {isGenerating ? (
              <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Sparkles size={12} />
            )}
            {isGenerating ? 'Generating...' : (suggestions?.suggestions ? 'Refresh suggestions' : 'Get suggestions')}
          </button>
        </div>

        <SuggestionCallout />
      </div>
    );
  }

  return (
    <div
      style={{
        marginBottom: '20px',
        cursor: 'pointer'
      }}
      onClick={handleStartEdit}
    >
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '11px',
        fontWeight: 700,
        color: '#78716c',
        marginBottom: '8px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
      }}>
        {label}
        {((aiSuggestions?.suggestions && aiSuggestions.suggestions.length > 0) || (suggestions?.suggestions && suggestions.suggestions.length > 0)) && (
          <div style={{
            background: '#fbbf24',
            borderRadius: '4px',
            padding: '2px 6px',
            fontSize: '10px',
            fontWeight: 600,
            color: '#92400e',
            display: 'flex',
            alignItems: 'center',
            gap: '3px'
          }}>
            <Sparkles size={10} />
            AI
          </div>
        )}
      </label>
      <div style={{
        padding: '12px',
        background: '#fafaf9',
        border: '2px solid transparent',
        borderRadius: '8px',
        minHeight: isTextarea ? (value ? 'auto' : '80px') : '44px',
        transition: 'all 0.2s',
        position: 'relative'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'white';
        e.currentTarget.style.borderColor = '#e7e5e4';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '#fafaf9';
        e.currentTarget.style.borderColor = 'transparent';
      }}>
        <div style={{
          fontSize: '14px',
          color: value ? '#0f172a' : '#a8a29e',
          whiteSpace: 'pre-wrap',
          overflow: 'visible',
          lineHeight: '1.5',
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }}>
          {value || placeholder}
        </div>
      </div>
    </div>
  );
};

export default EnhancedFieldWithSuggestions;